// src/pages/LandingPage.jsx
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'

export default function LandingPage() {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll)

    // PWA install prompt
    const onPrompt = e => { e.preventDefault(); setDeferredPrompt(e) }
    window.addEventListener('beforeinstallprompt', onPrompt)

    // Scroll reveal
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
        }
      })
    }, { threshold: 0.1 })
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el))

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('beforeinstallprompt', onPrompt)
      observer.disconnect()
    }
  }, [])

  async function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      await deferredPrompt.userChoice
      setDeferredPrompt(null)
    } else {
      navigate('/app')
    }
  }

  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Mono:wght@300;400;500&display=swap');
        .reveal { opacity: 0; transform: translateY(20px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .reveal.visible { opacity: 1; transform: translateY(0); }
        .hover-gold:hover { color: #c8a96e !important; }
        .btn-gold:hover { background: #e8c98e !important; transform: translateY(-1px); }
        .btn-outline:hover { border-color: rgba(200,169,110,0.4) !important; color: #c8a96e !important; }
        .feature-card:hover { border-color: rgba(200,169,110,0.3) !important; }
        .install-badge:hover { border-color: rgba(200,169,110,0.3) !important; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        .fade-1 { animation: fadeUp 0.7s 0.0s ease both; }
        .fade-2 { animation: fadeUp 0.7s 0.1s ease both; }
        .fade-3 { animation: fadeUp 0.7s 0.2s ease both; }
        .fade-4 { animation: fadeUp 0.7s 0.3s ease both; }
        .fade-5 { animation: fadeUp 0.7s 0.4s ease both; }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ ...s.nav, background: scrolled ? 'rgba(10,10,10,0.95)' : 'rgba(10,10,10,0.7)', backdropFilter: 'blur(12px)' }}>
        <div style={s.navLogo}>
          <div style={s.navIcon}>
            <CrosshairIcon size={20} color="#0a0a0a" />
          </div>
          <div>
            <div style={s.navName}>DISDownrange</div>
            <div style={s.navSub}>by Dynamic Innovative Solutions</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <a href="#pricing" style={{ ...s.navLink }} className="hover-gold">Pricing</a>
          <button style={s.navCta} className="btn-gold" onClick={handleInstall}>
            Launch App →
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={s.hero}>
        {/* Crosshair bg */}
        <svg style={s.heroBg} viewBox="0 0 500 500" fill="none" stroke="white" strokeWidth="0.6">
          <circle cx="250" cy="250" r="220"/><circle cx="250" cy="250" r="160"/>
          <circle cx="250" cy="250" r="100"/><circle cx="250" cy="250" r="50"/>
          <line x1="250" y1="0" x2="250" y2="500"/><line x1="0" y1="250" x2="500" y2="250"/>
          <circle cx="250" cy="250" r="5" fill="white"/>
        </svg>

        <div style={s.heroContent}>
          <div style={s.eyebrow} className="fade-1">Dynamic Innovative Solutions LLC — Ballistic Advisor</div>
          <h1 style={s.heroTitle} className="fade-2">
            Know Your <span style={{ color:'#c8a96e' }}>Round.</span><br/>
            Own Your <span style={{ color:'#c8a96e' }}>Shot.</span>
          </h1>
          <p style={s.heroSub} className="fade-3">
            The only ballistic app that combines trajectory calculations, terminal performance ratings, and real-time weather — built by a named inventor on 25+ US patents.
          </p>
          <div style={s.heroBtns} className="fade-4">
            <button style={s.btnPrimary} className="btn-gold" onClick={handleInstall}>
              <span>↓</span> Install Free App
            </button>
            <a href="#features" style={s.btnOutline} className="btn-outline">See Features →</a>
          </div>
          <div style={s.heroNote} className="fade-5">
            Free to install · No app store required · Works on any device
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div style={s.statsBar}>
        {[
          { num: '25+', label: 'Granted US Patents' },
          { num: '0',   label: 'Competitors With Terminal Data' },
          { num: '15+', label: 'Curated Ammo Profiles' },
          { num: '$0',  label: 'To Get Started' },
        ].map(({ num, label }) => (
          <div key={label} style={s.stat}>
            <div style={s.statNum}>{num}</div>
            <div style={s.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── THE GAP ── */}
      <section style={{ ...s.section, background: '#111' }}>
        <div style={s.container}>
          <div style={s.sectionEyebrow} className="reveal">Market Gap</div>
          <h2 style={s.sectionTitle} className="reveal">What Every Other App is Missing</h2>
          <p style={s.sectionSub} className="reveal">
            Hornady 4DOF, Applied Ballistics, and Strelok give you drop tables. None tell you what happens when the bullet arrives.
          </p>

          <div style={s.gapGrid} className="reveal">
            <div style={{ ...s.gapHeader, color: '#444' }}>Other Ballistic Apps</div>
            <div style={{ ...s.gapHeader, color: '#c8a96e', background: 'rgba(200,169,110,0.08)' }}>DISDownrange</div>
            {[
              ['Drop tables', 'Drop tables'],
              ['Weather integration', 'Real-time GPS weather'],
              [null, 'Wound channel ratings'],
              [null, 'FBI protocol penetration data'],
              [null, 'Barrier performance ratings'],
              [null, 'Ethical hunting range'],
              [null, 'Use case filter (hunt/defense/range)'],
              [null, 'Auto twist compatibility check'],
              [null, 'Barrel length MV correction'],
            ].map(([them, us], i) => (
              <React.Fragment key={i}>
                <div style={{ ...s.gapCell, color: them ? '#888' : '#333' }}>
                  <span style={{ color: them ? '#4caf7d' : '#333' }}>{them ? '✓' : '✕'}</span> {them || 'Not available'}
                </div>
                <div style={{ ...s.gapCell, background: 'rgba(200,169,110,0.03)' }}>
                  <span style={{ color: '#4caf7d' }}>✓</span> {us}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={s.section}>
        <div style={s.container}>
          <div style={s.sectionEyebrow} className="reveal">Features</div>
          <h2 style={s.sectionTitle} className="reveal">Built for Hunters,<br/>Defenders, and Precision Shooters</h2>

          <div style={s.featuresGrid}>
            {FEATURES.map(f => (
              <div key={f.title} style={s.featureCard} className="reveal feature-card">
                <div style={s.featureIconWrap}>{f.icon}</div>
                <div style={s.featureTitle}>{f.title}</div>
                <div style={s.featureDesc}>{f.desc}</div>
                <div style={{ ...s.featureTag, ...TIER_STYLES[f.tier] }}>{f.tier}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW TO INSTALL ── */}
      <section style={{ ...s.section, background: '#111' }}>
        <div style={s.container}>
          <div style={s.sectionEyebrow} className="reveal">Installation</div>
          <h2 style={s.sectionTitle} className="reveal">Install in 30 Seconds.<br/>No App Store Required.</h2>
          <p style={s.sectionSub} className="reveal">Works on iPhone, Android, and desktop — directly from your browser.</p>

          <div style={s.steps}>
            {STEPS.map((step, i) => (
              <div key={i} style={s.step} className="reveal">
                <div style={s.stepNum}>{i + 1}</div>
                <div>
                  <div style={s.stepTitle}>{step.title}</div>
                  <div style={s.stepDesc}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={s.section}>
        <div style={s.container}>
          <div style={s.sectionEyebrow} className="reveal">Pricing</div>
          <h2 style={s.sectionTitle} className="reveal">Start Free. Upgrade When Ready.</h2>

          <div style={s.pricingGrid}>
            {PLANS.map(plan => (
              <div key={plan.name} style={{ ...s.priceCard, ...(plan.featured ? s.priceCardFeatured : {}) }} className="reveal">
                {plan.featured && <div style={s.popularBadge}>Most Popular</div>}
                <div style={s.priceName}>{plan.emoji} {plan.name}</div>
                <div style={s.priceAmount}>
                  {plan.price === 0 ? 'Free' : `$${plan.price}`}
                  {plan.price > 0 && <span style={{ fontSize:13, color:'#888', fontFamily:'DM Mono, monospace' }}>/mo or ${plan.yearly}/yr</span>}
                </div>
                <div style={s.priceDesc}>{plan.desc}</div>
                <div style={s.priceFeatures}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ ...s.pf, color: f.included ? '#aaa' : '#444' }}>
                      <span style={{ color: f.included ? '#4caf7d' : '#444' }}>{f.included ? '✓' : '✕'}</span>
                      {f.text}
                    </div>
                  ))}
                </div>
                <button
                  style={{ ...s.priceBtn, ...(plan.featured ? s.priceBtnGold : s.priceBtnOutline) }}
                  className={plan.featured ? 'btn-gold' : 'btn-outline'}
                  onClick={handleInstall}
                >
                  {plan.price === 0 ? 'Get Started Free →' : `Get ${plan.name} →`}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INSTALL CTA ── */}
      <section style={{ ...s.section, textAlign:'center', background: 'radial-gradient(ellipse at center, rgba(200,169,110,0.07) 0%, transparent 70%)' }}>
        <div style={s.container}>
          <div style={s.sectionEyebrow} className="reveal">Get DISDownrange</div>
          <h2 style={{ ...s.sectionTitle, fontSize:'clamp(32px, 6vw, 54px)' }} className="reveal">
            Install on Any Device.<br/>Free to Start.
          </h2>
          <div style={s.installBadges} className="reveal">
            {[
              { icon:'📱', top:'Add to Home Screen', main:'iPhone / iOS' },
              { icon:'🤖', top:'Install App', main:'Android' },
              { icon:'💻', top:'Open in Browser', main:'Desktop / Web' },
            ].map(b => (
              <button key={b.main} style={s.installBadge} className="install-badge" onClick={handleInstall}>
                <div style={{ fontSize:28 }}>{b.icon}</div>
                <div>
                  <div style={{ fontSize:9, color:'#666', textTransform:'uppercase', letterSpacing:'0.1em' }}>{b.top}</div>
                  <div style={{ fontSize:14, color:'#f0ede8', fontFamily:'Playfair Display, serif', fontWeight:700, marginTop:2 }}>{b.main}</div>
                </div>
              </button>
            ))}
          </div>
          <p style={s.installNote} className="reveal">
            No app store required. No 30% cut to Apple or Google.<br/>
            Direct from Dynamic Innovative Solutions LLC.
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={s.footer}>
        <div style={{ fontFamily:'Playfair Display, serif', fontSize:15, color:'#f0ede8', marginBottom:6 }}>DISDownrange</div>
        <div style={{ fontSize:10, color:'#444', letterSpacing:'0.1em', textTransform:'uppercase' }}>by Dynamic Innovative Solutions LLC</div>
        <div style={s.footerLinks}>
          <button style={s.footerLink} className="hover-gold" onClick={handleInstall}>Launch App</button>
          <a href="#features" style={s.footerLink} className="hover-gold">Features</a>
          <a href="#pricing" style={s.footerLink} className="hover-gold">Pricing</a>
        </div>
        <div style={{ fontSize:10, color:'#333', marginTop:16, lineHeight:1.7 }}>
          © 2026 Dynamic Innovative Solutions LLC · All rights reserved<br/>
          Named inventor on 25+ granted US patents · NASA · DARPA · FAA collaboration
        </div>
      </footer>

      <Footer />
    </div>
  )
}

// ── Icons ──
function CrosshairIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="7"/>
      <line x1="12" y1="2" x2="12" y2="5"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
      <line x1="2" y1="12" x2="5" y2="12"/>
      <line x1="19" y1="12" x2="22" y2="12"/>
      <circle cx="12" cy="12" r="1.5" fill={color}/>
    </svg>
  )
}

// ── Data ──
const FEATURES = [
  { title:'Ballistic Drop Table', desc:'G1 point-mass solver with MV auto-corrected for your exact barrel length. Drop, velocity, energy, and wind drift at every distance.', tier:'Free', icon:'📉' },
  { title:'Real-Time GPS Weather', desc:'One tap auto-fills temperature, altitude, wind speed and direction from your exact GPS location via Open-Meteo. No manual entry in the field.', tier:'Pro', icon:'🌤' },
  { title:'Terminal Performance Ratings', desc:'Wound channel type, penetration depth 1–5, barrier performance score, FBI protocol flag, and LE adoption status for every round.', tier:'Pro', icon:'⚡' },
  { title:'Ethical Hunting Range', desc:'Calculates the maximum range where each round retains minimum expansion velocity. Know your ethical ceiling before you shoot.', tier:'Pro', icon:'🦌' },
  { title:'Barrel/Twist Profile Builder', desc:'Define any rifle by barrel length and twist rate. App auto-warns when bullet weight is incompatible with your twist rate.', tier:'Free', icon:'🔧' },
  { title:'Side-by-Side Comparison', desc:'Compare up to 4 rounds head-to-head across all terminal metrics. Know which round is right for your specific use case before you buy.', tier:'Pro', icon:'⚖️' },
]

const TIER_STYLES = {
  Free:  { background:'rgba(76,175,125,0.12)',  color:'#4caf7d', border:'1px solid rgba(76,175,125,0.2)'  },
  Pro:   { background:'rgba(91,155,213,0.12)',  color:'#5b9bd5', border:'1px solid rgba(91,155,213,0.2)'  },
  Elite: { background:'rgba(200,169,110,0.12)', color:'#c8a96e', border:'1px solid rgba(200,169,110,0.2)' },
}

const STEPS = [
  { title:'Open disdownrange.org in your browser', desc:'Works in Chrome, Safari, Edge, and Firefox. Use your phone browser for the best mobile experience.' },
  { title:'Tap "Add to Home Screen"', desc:'iPhone: Share button → "Add to Home Screen." Android: three-dot menu → "Install App."' },
  { title:'Create your free account', desc:'Sign up with your email. Rifle profiles and settings sync across all your devices.' },
  { title:'Add your rifle and start calculating', desc:'Enter barrel length, twist rate, and zero distance. DISDownrange auto-corrects MV and warns you about incompatible loads.' },
]

const PLANS = [
  {
    name:'Downrange Basic', emoji:'🎯', price:0, yearly:0, featured:false,
    desc:'Start calculating trajectories. Free forever.',
    features:[
      { text:'2 rifle profiles', included:true },
      { text:'Basic ammo library', included:true },
      { text:'Drop table calculator', included:true },
      { text:'Terminal performance ratings', included:false },
      { text:'Real-time GPS weather', included:false },
    ],
  },
  {
    name:'Pro', emoji:'⚡', price:4.99, yearly:34.99, featured:true,
    desc:'The full DISDownrange experience for serious shooters.',
    features:[
      { text:'Everything in Free', included:true },
      { text:'Unlimited rifle profiles', included:true },
      { text:'Full terminal performance data', included:true },
      { text:'Real-time GPS weather', included:true },
      { text:'Ethical range calculator', included:true },
      { text:'Side-by-side comparison', included:true },
    ],
  },
  {
    name:'Elite', emoji:'💎', price:9.99, yearly:69.99, featured:false,
    desc:'Everything in Pro plus advanced features.',
    features:[
      { text:'Everything in Pro', included:true },
      { text:'Shot log & auto-true model', included:true },
      { text:'Terrain presets', included:true },
      { text:'Priority ammo updates', included:true },
      { text:'First access to Phase 2', included:true },
    ],
  },
]

// ── Styles ──
const s = {
  page:     { background:'#0a0a0a', color:'#f0ede8', fontFamily:"'DM Mono', monospace", overflowX:'hidden' },
  nav:      { position:'fixed', top:0, left:0, right:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 40px', borderBottom:'1px solid rgba(255,255,255,0.06)', transition:'background 0.3s' },
  navLogo:  { display:'flex', alignItems:'center', gap:12 },
  navIcon:  { width:34, height:34, background:'#c8a96e', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  navName:  { fontFamily:"'Playfair Display', serif", fontSize:16, fontWeight:700, color:'#f0ede8' },
  navSub:   { fontSize:9, color:'#c8a96e', letterSpacing:'0.12em', textTransform:'uppercase' },
  navLink:  { fontSize:12, color:'#888', textDecoration:'none', transition:'color 0.2s', fontFamily:"'DM Mono', monospace" },
  navCta:   { background:'#c8a96e', color:'#0a0a0a', border:'none', padding:'9px 22px', borderRadius:7, fontSize:12, fontWeight:500, fontFamily:"'DM Mono', monospace", cursor:'pointer', transition:'all 0.2s', letterSpacing:'0.04em' },

  hero:        { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'120px 24px 80px', position:'relative', textAlign:'center' },
  heroBg:      { position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'min(700px,90vw)', height:'min(700px,90vw)', opacity:0.04, pointerEvents:'none' },
  heroContent: { position:'relative', zIndex:1, maxWidth:680 },
  eyebrow:     { fontSize:11, letterSpacing:'0.2em', textTransform:'uppercase', color:'#c8a96e', marginBottom:24 },
  heroTitle:   { fontFamily:"'Playfair Display', serif", fontSize:'clamp(42px,8vw,84px)', fontWeight:900, lineHeight:1.0, marginBottom:20 },
  heroSub:     { fontSize:'clamp(13px,2.5vw,15px)', color:'#888', maxWidth:520, margin:'0 auto 44px', lineHeight:1.7 },
  heroBtns:    { display:'flex', gap:14, flexWrap:'wrap', justifyContent:'center' },
  heroNote:    { marginTop:20, fontSize:11, color:'#444' },
  btnPrimary:  { display:'inline-flex', alignItems:'center', gap:10, background:'#c8a96e', color:'#0a0a0a', padding:'14px 30px', borderRadius:10, fontFamily:"'Playfair Display', serif", fontSize:15, fontWeight:700, border:'none', cursor:'pointer', transition:'all 0.2s', letterSpacing:'0.02em' },
  btnOutline:  { display:'inline-flex', alignItems:'center', gap:10, background:'transparent', color:'#f0ede8', padding:'14px 30px', borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', fontFamily:"'DM Mono', monospace", fontSize:13, cursor:'pointer', transition:'all 0.2s', textDecoration:'none', letterSpacing:'0.04em' },

  statsBar:  { background:'#111', borderTop:'1px solid rgba(255,255,255,0.07)', borderBottom:'1px solid rgba(255,255,255,0.07)', padding:'24px 40px', display:'flex', justifyContent:'center', flexWrap:'wrap' },
  stat:      { flex:1, minWidth:140, maxWidth:220, textAlign:'center', padding:'10px 16px', borderRight:'1px solid rgba(255,255,255,0.07)' },
  statNum:   { fontFamily:"'Playfair Display', serif", fontSize:30, fontWeight:900, color:'#c8a96e' },
  statLabel: { fontSize:10, color:'#666', letterSpacing:'0.1em', textTransform:'uppercase', marginTop:4 },

  section:       { padding:'90px 24px' },
  container:     { maxWidth:960, margin:'0 auto' },
  sectionEyebrow:{ fontSize:10, letterSpacing:'0.2em', textTransform:'uppercase', color:'#c8a96e', marginBottom:14 },
  sectionTitle:  { fontFamily:"'Playfair Display', serif", fontSize:'clamp(26px,5vw,46px)', fontWeight:900, color:'#f0ede8', marginBottom:14, lineHeight:1.1 },
  sectionSub:    { fontSize:13, color:'#888', maxWidth:520, lineHeight:1.7, marginBottom:0 },

  gapGrid:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, marginTop:44, background:'rgba(255,255,255,0.07)', borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,255,255,0.07)' },
  gapHeader: { padding:'12px 18px', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:500, background:'rgba(255,255,255,0.03)' },
  gapCell:   { padding:'10px 18px', fontSize:12, display:'flex', alignItems:'center', gap:8, borderTop:'1px solid rgba(255,255,255,0.05)', background:'#0a0a0a' },

  featuresGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:14, marginTop:44 },
  featureCard:  { background:'#141414', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:22, transition:'border-color 0.2s' },
  featureIconWrap: { fontSize:26, marginBottom:12 },
  featureTitle: { fontSize:14, fontWeight:500, color:'#f0ede8', marginBottom:8, fontFamily:"'Playfair Display', serif" },
  featureDesc:  { fontSize:12, color:'#888', lineHeight:1.6 },
  featureTag:   { display:'inline-block', marginTop:12, fontSize:9, padding:'3px 8px', borderRadius:4, letterSpacing:'0.08em', textTransform:'uppercase' },

  steps:    { display:'flex', flexDirection:'column', marginTop:44 },
  step:     { display:'flex', gap:24, padding:'24px 0', borderBottom:'1px solid rgba(255,255,255,0.07)' },
  stepNum:  { width:42, height:42, borderRadius:'50%', flexShrink:0, background:'rgba(200,169,110,0.1)', border:'1px solid rgba(200,169,110,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Playfair Display', serif", fontSize:18, fontWeight:900, color:'#c8a96e' },
  stepTitle:{ fontSize:15, fontWeight:500, color:'#f0ede8', marginBottom:6, fontFamily:"'Playfair Display', serif" },
  stepDesc: { fontSize:12, color:'#888', lineHeight:1.6 },

  pricingGrid:      { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(250px, 1fr))', gap:14, marginTop:44 },
  priceCard:        { background:'#141414', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:26, display:'flex', flexDirection:'column', gap:16, position:'relative' },
  priceCardFeatured:{ border:'1px solid rgba(200,169,110,0.4)', background:'linear-gradient(145deg, rgba(200,169,110,0.06), rgba(0,0,0,0))' },
  popularBadge:     { position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:'#c8a96e', color:'#0a0a0a', fontSize:10, fontWeight:700, padding:'3px 14px', borderRadius:4, letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap' },
  priceName:        { fontFamily:"'Playfair Display', serif", fontSize:18, fontWeight:700, color:'#f0ede8' },
  priceAmount:      { fontFamily:"'Playfair Display', serif", fontSize:34, fontWeight:900, color:'#c8a96e' },
  priceDesc:        { fontSize:11, color:'#888', lineHeight:1.5 },
  priceFeatures:    { display:'flex', flexDirection:'column', gap:8, flex:1 },
  pf:               { display:'flex', alignItems:'center', gap:10, fontSize:12 },
  priceBtn:         { display:'block', textAlign:'center', padding:12, borderRadius:8, fontSize:13, fontWeight:500, fontFamily:"'DM Mono', monospace", letterSpacing:'0.04em', cursor:'pointer', transition:'all 0.2s', border:'none' },
  priceBtnGold:     { background:'#c8a96e', color:'#0a0a0a' },
  priceBtnOutline:  { background:'transparent', color:'#888', border:'1px solid rgba(255,255,255,0.1)' },

  installBadges: { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', marginTop:36 },
  installBadge:  { display:'flex', alignItems:'center', gap:12, background:'#141414', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 22px', cursor:'pointer', transition:'border-color 0.2s' },
  installNote:   { fontSize:11, color:'#444', marginTop:22, lineHeight:1.7, maxWidth:400, marginLeft:'auto', marginRight:'auto' },

  footer:      { background:'#111', borderTop:'1px solid rgba(255,255,255,0.07)', padding:40, textAlign:'center' },
  footerLinks: { display:'flex', gap:22, justifyContent:'center', marginTop:18 },
  footerLink:  { fontSize:11, color:'#888', textDecoration:'none', background:'none', border:'none', cursor:'pointer', fontFamily:"'DM Mono', monospace", transition:'color 0.2s' },
}
